import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { evalReportsQuery, runDetailQuery, runsQuery } from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import { usePersonZone, whenIn } from '../lib/personTime'
import { LoadProblem } from '../shell/LoadProblem'
import type { EvalRunReport, RunSummary } from '../api/types'

export const Route = createFileRoute('/_shell/runs')({
  component: RunsView,
})

/**
 * The Runs surface (wave-5 step 6) — internal eyes on the instrument: every
 * model run the substrate has persisted since wave 2 (outcomes with refusals
 * included, tokens, cost — ADR-0024's measured stream), and the live eval
 * runner's persisted results beside them. INTERNAL BY DESIGN: no rail item
 * mounts this route (the nav's eight-item charter is the design lane's);
 * the door is the URL — /runs — which still beats the old doors (psql and
 * test output) by every measure that matters. Read-only throughout; the
 * traces are the record, and nothing here takes a verb.
 */
function RunsView() {
  const { id: engagementId } = useEngagement()
  const runs = useQuery(runsQuery(engagementId))
  const reports = useQuery(evalReportsQuery)
  const [openRun, setOpenRun] = useState<string | null>(null)
  const [openReport, setOpenReport] = useState<string | null>(null)

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <div>
          <h1>Runs</h1>
          <p className="surface-sub">
            The instrument's own record: every model run with its outcome and cost, and the
            eval runner's persisted results. Internal; reading is the only verb.
          </p>
        </div>
      </header>

      {runs.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={runs.error} />
      {runs.data?.length === 0 && (
        <p className="surface-note">No model runs yet in this engagement.</p>
      )}

      <div className="runs-list">
        {runs.data?.map((run) => (
          <RunRow
            key={run.id}
            engagementId={engagementId}
            run={run}
            open={openRun === run.id}
            onToggle={() => setOpenRun((current) => (current === run.id ? null : run.id!))}
          />
        ))}
      </div>

      <h2 className="surface-h2 runs-evals-head">Eval runs</h2>
      {reports.data?.length === 0 && (
        <p className="surface-note">
          No persisted eval runs yet. The live runner persists here when JEDI_EVAL_STORE
          points at this store; the dated markdown stays the workstation's copy.
        </p>
      )}
      <div className="runs-list">
        {reports.data?.map((report) => (
          <EvalReportRow
            key={report.id}
            report={report}
            open={openReport === report.id}
            onToggle={() =>
              setOpenReport((current) => (current === report.id ? null : report.id!))
            }
          />
        ))}
      </div>
    </section>
  )
}

function RunRow({
  engagementId,
  run,
  open,
  onToggle,
}: {
  engagementId: string
  run: RunSummary
  open: boolean
  onToggle: () => void
}) {
  const zone = usePersonZone()
  // The drill fetches the full trace only when opened — step payloads carry
  // whole prompts, and the list stays light without them.
  const detail = useQuery({ ...runDetailQuery(engagementId, run.id!), enabled: open })

  return (
    <div className="runs-row-block">
      <button type="button" className="runs-row" onClick={onToggle}>
        <span className={`plan-tag ${run.outcome === 'drafted' || run.outcome === 'answered' ? 'plan-state' : ''}`}>
          {run.outcome}
        </span>
        <span className="runs-intent">{run.intent}</span>
        <span className="runs-meta">
          {run.stepCount} step{run.stepCount === 1 ? '' : 's'} · {run.inputTokens}→{run.outputTokens} tok
          · ${Number(run.costUsd ?? 0).toFixed(4)} · {whenIn(run.at!, zone)}
        </span>
      </button>

      {open && detail.isPending && <p className="surface-note">Loading the trace…</p>}
      {open && detail.data && (
        <div className="run-steps">
          {detail.data.steps?.map((step, index) => (
            <div key={index} className="run-step">
              <p className="runs-meta">
                step {index + 1} · {step.inputTokens}→{step.outputTokens} tok
              </p>
              {step.messages?.map((message, m) => (
                <pre key={m} className="run-text">
                  <span className="run-role">{message.role}</span>
                  {'\n'}
                  {message.content}
                </pre>
              ))}
              <pre className="run-text run-reply">
                <span className="run-role">reply</span>
                {'\n'}
                {step.reply}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The verdict word and its colour, three-state since the wave-8 instrument
 * repair (ruled 2026-09-01). `outcome` is additive-null on the persisted row:
 * runs recorded before the repair — and every M=1 run since — carry null, and
 * fall back to the boolean they do have. A split is deliberately neither
 * green nor alarming; it is the instrument saying the case is unsettled.
 */
function verdictWord(outcome: string | null | undefined, pass: boolean | undefined) {
  if (outcome === 'split') return 'split'
  return pass ? 'pass' : 'FAIL'
}

function verdictClass(outcome: string | null | undefined, pass: boolean | undefined) {
  if (outcome === 'split') return 'plan-tag runs-split'
  return pass ? 'plan-tag plan-state' : 'plan-tag runs-fail'
}

function EvalReportRow({
  report,
  open,
  onToggle,
}: {
  report: EvalRunReport
  open: boolean
  onToggle: () => void
}) {
  const zone = usePersonZone()
  return (
    <div className="runs-row-block">
      <button type="button" className="runs-row" onClick={onToggle}>
        <span className="plan-tag">{report.setName}</span>
        <span className="runs-intent">
          {report.passed}/{report.total} passed
          {report.split ? ` · ${report.split} split` : ''}
        </span>
        <span className="runs-meta">
          ${Number(report.costUsd ?? 0).toFixed(4)} · {whenIn(report.ranAtUtc!, zone)}
        </span>
      </button>

      {open && (
        <div className="run-steps">
          {report.lines?.map((line) => (
            <p key={line.caseId} className="runs-eval-line">
              <span className={verdictClass(line.outcome, line.pass)}>
                {verdictWord(line.outcome, line.pass)}
              </span>{' '}
              <span className="runs-meta">{line.caseId}</span> {line.detail}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
